import React, { FC } from 'react'
import styles from './PolaroidList.module.scss'
import Polaroid, { PolaroidSpecificPageProps } from './Polaroid'
import { SAArtwork } from 'types/Artwork'
import { PolaroidListSkeleton } from './components/PolaroidSkeleton'
import classNames from 'classnames'
import useQA from 'hooks/useQA'

type PolaroidListProps = PolaroidSpecificPageProps & {
  artworks: ReadonlyArray<SAArtwork>
  isLoading?: boolean
  isArtistNameVisible?: boolean
  isPrintsFromVisible?: boolean
  skeletonQuantity?: number
  userID?: number
  isUsersArtworksAccount?: boolean
  customStyle?: string
  isMediumMaterialVisible?: boolean
  isDimensionsVisible?: boolean
  customPriceElement?: (artwork: SAArtwork) => JSX.Element
  customActions?: (artwork: SAArtwork) => JSX.Element
  customPromoCode?: string
  showPrintsFromInPrice?: boolean
  showOriginalPrice?: boolean
  hasPrints?: boolean
  onCheckArtworkToBuildLayout?: (artwork: SAArtwork, isPrintsFromVisible?: boolean) => boolean
  onCheckIsPrintForDataLayerEvent?: (artwork: SAArtwork) => boolean
}

const PolaroidList: FC<PolaroidListProps> = ({
  artworks,
  algoliaIndice,
  algoliaUserToken,
  dataApi,
  dataList,
  dataListId,
  dataListName,
  isLoading = false,
  skeletonQuantity = 6,
  isArtistNameVisible = true,
  isPrintsFromVisible = true,
  userID,
  isMediumMaterialVisible,
  isDimensionsVisible,
  isUsersArtworksAccount,
  customStyle = '',
  customPriceElement,
  customActions,
  customPromoCode,
  showPrintsFromInPrice,
  showOriginalPrice,
  hasPrints,
  onCheckArtworkToBuildLayout,
  onCheckIsPrintForDataLayerEvent,
}) => {
  const { testId } = useQA()

  if (isLoading) {
    return <PolaroidListSkeleton skeletonQuantity={skeletonQuantity} customStyle={customStyle} />
  }

  if (!artworks?.length) {
    return null
  }

  return (
    <div
      className={classNames(styles.polaroidListContainer, {
        [customStyle]: customStyle !== '',
      })}
      {...testId('polaroid-list-container')}
    >
      {artworks.map((artwork, index) => {
        const priority = index === 0
        const lazyLoad = index > 3
        const isImageSrcFromProd = index < 6
        return (
          <Polaroid
            key={artwork.artworkId}
            artwork={artwork}
            algoliaIndice={algoliaIndice}
            algoliaUserToken={algoliaUserToken}
            dataApi={dataApi}
            dataList={dataList}
            dataListId={dataListId}
            dataListName={dataListName}
            isArtistNameVisible={isArtistNameVisible}
            priority={priority}
            lazyLoad={lazyLoad}
            isImageSrcFromProd={isImageSrcFromProd}
            userID={userID}
            isUsersArtworksAccount={isUsersArtworksAccount}
            customPriceElement={!!customPriceElement ? customPriceElement(artwork) : undefined}
            customActions={!!customActions ? customActions(artwork) : undefined}
            customPromoCode={!!customPromoCode ? customPromoCode : ''}
            isPrintsFromVisible={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout(artwork, isPrintsFromVisible)
                : isPrintsFromVisible
            }
            isMediumMaterialVisible={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout(artwork, isMediumMaterialVisible)
                : isMediumMaterialVisible
            }
            isDimensionsVisible={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout(artwork, isDimensionsVisible)
                : isDimensionsVisible
            }
            showPrintsFromInPrice={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout(artwork, showPrintsFromInPrice)
                : showPrintsFromInPrice
            }
            showOriginalPrice={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout(artwork, showOriginalPrice)
                : showOriginalPrice
            }
            hasPrints={
              !!onCheckArtworkToBuildLayout
                ? onCheckArtworkToBuildLayout(artwork, hasPrints)
                : hasPrints
            }
            isPrintForDataLayerEvent={
              !!onCheckIsPrintForDataLayerEvent ? onCheckIsPrintForDataLayerEvent(artwork) : false
            }
          />
        )
      })}
    </div>
  )
}

export default PolaroidList
